import {
  CheckIcon,
  XIcon,
  ClockIcon,
  CheckCircleFillIcon,
  FileDiffIcon,
  NoEntryFillIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import type {CSSProperties} from 'react'
import {Tooltip} from '@primer/react'
import {ReviewDecisionState, StatusCheckState, type StatusCheckRollup} from '../types/list-types'
import {reviewDecisionTooltipLabel, type ReviewDecisionCounts} from '../consts/review-decision-consts'
import {NonInteractiveTooltip} from './NonInteractiveTooltip'
import styles from '../PullsListItem.module.css'

const inlineFlexCenter: CSSProperties = {display: 'inline-flex', alignItems: 'center'}

type ReviewFilterProps = {
  onClick: () => void
  className?: string
}

export const statusCheckDisplay = (statusCheckRollup: StatusCheckRollup) => {
  let statusIcon = null
  switch (statusCheckRollup.state) {
    case StatusCheckState.Success:
      statusIcon = <CheckIcon size={12} className="color-fg-success" />
      break
    case StatusCheckState.Failure:
      statusIcon = <XIcon size={12} className="color-fg-danger" />
      break
    case StatusCheckState.Pending:
      statusIcon = <ClockIcon size={12} className="color-fg-attention" />
      break
    default:
      return null
  }

  return (
    <span data-testid="ci-status-check" className="d-inline-flex flex-items-center">
      <span className="mr-1 ml-1">&middot;</span>
      {statusIcon} {statusCheckRollup.successCount}/{statusCheckRollup.totalCount}
    </span>
  )
}

export const statusCheckDisplayCompact = (statusCheckRollup: StatusCheckRollup) => {
  let icon = null
  let label = ''
  switch (statusCheckRollup.state) {
    case StatusCheckState.Success:
      icon = <CheckIcon size={16} className="color-fg-success" />
      label = `${statusCheckRollup.successCount}/${statusCheckRollup.totalCount} checks passing`
      break
    case StatusCheckState.Failure:
      icon = <XIcon size={16} className="color-fg-danger" />
      label = `${statusCheckRollup.successCount}/${statusCheckRollup.totalCount} checks passing`
      break
    case StatusCheckState.Pending:
      icon = <ClockIcon size={16} className="color-fg-attention" />
      label = `${statusCheckRollup.successCount}/${statusCheckRollup.totalCount} checks passing`
      break
    default:
      return null
  }

  return (
    <span role="img" aria-label={label} data-testid="ci-status-check-compact" style={inlineFlexCenter}>
      <span aria-hidden="true" style={inlineFlexCenter}>
        {icon}
      </span>
      <span className="ml-1">
        {statusCheckRollup.successCount}/{statusCheckRollup.totalCount}
      </span>
    </span>
  )
}

export const reviewDecision = (
  reviewDecisionState: ReviewDecisionState,
  counts: ReviewDecisionCounts = {},
  filterProps?: ReviewFilterProps,
) => {
  let reviewDecisionIcon = null
  let label = ''
  const wrapperClass = 'd-inline-flex flex-items-center gap-1'
  switch (reviewDecisionState) {
    case ReviewDecisionState.Approved:
      label = 'Approved'
      reviewDecisionIcon = (
        <span className={wrapperClass}>
          <CheckCircleFillIcon size={12} className="color-fg-success" />
          <span className={styles.reviewDecisionLabel}>{label}</span>
        </span>
      )
      break
    case ReviewDecisionState.ChangesRequested:
      label = 'Changes requested'
      reviewDecisionIcon = (
        <span className={wrapperClass}>
          <XCircleFillIcon size={12} className="color-fg-danger" />
          <span className={styles.reviewDecisionLabel}>{label}</span>
        </span>
      )
      break
    case ReviewDecisionState.ReviewRequired:
      label = 'Review required'
      reviewDecisionIcon = (
        <span className={wrapperClass}>
          <NoEntryFillIcon size={12} className="color-fg-attention" />
          <span className={styles.reviewDecisionLabel}>{label}</span>
        </span>
      )
      break
    default:
      return null
  }

  const tooltipLabel = reviewDecisionTooltipLabel(reviewDecisionState, counts)

  return (
    <span data-testid="review-decision-icon" className="d-inline-flex flex-items-center">
      <span className="mr-1 ml-1">&middot;</span>
      {filterProps ? (
        <Tooltip text={tooltipLabel ?? ''} direction="s" type="description">
          <button type="button" {...filterProps} aria-label={`Filter by review: ${label}`}>
            {reviewDecisionIcon}
          </button>
        </Tooltip>
      ) : tooltipLabel ? (
        <NonInteractiveTooltip label={tooltipLabel} direction="s">
          {reviewDecisionIcon}
        </NonInteractiveTooltip>
      ) : (
        reviewDecisionIcon
      )}
    </span>
  )
}

export const reviewDecisionCompact = (
  reviewDecisionState: ReviewDecisionState,
  counts: ReviewDecisionCounts = {},
  filterProps?: ReviewFilterProps,
) => {
  let icon = null
  let label = ''
  switch (reviewDecisionState) {
    case ReviewDecisionState.Approved:
      icon = <CheckCircleFillIcon size={16} className="color-fg-success" />
      label = 'Approved'
      break
    case ReviewDecisionState.ChangesRequested:
      icon = <FileDiffIcon size={16} className="color-fg-danger" />
      label = 'Changes requested'
      break
    case ReviewDecisionState.ReviewRequired:
      icon = <NoEntryFillIcon size={16} className="color-fg-attention" />
      label = 'Review required'
      break
    default:
      return null
  }

  const tooltipLabel = reviewDecisionTooltipLabel(reviewDecisionState, counts)

  if (filterProps) {
    return (
      <Tooltip text={tooltipLabel ?? label} direction="s" type="description">
        <button
          type="button"
          {...filterProps}
          aria-label={`Filter by review: ${label}`}
          data-testid="review-decision-compact"
          style={inlineFlexCenter}
        >
          {icon}
        </button>
      </Tooltip>
    )
  }

  return (
    <NonInteractiveTooltip
      label={tooltipLabel ?? label}
      direction="s"
      role="img"
      aria-label={tooltipLabel ?? label}
      data-testid="review-decision-compact"
      style={inlineFlexCenter}
      includeAriaAttributes={false}
    >
      <span aria-hidden="true">{icon}</span>
    </NonInteractiveTooltip>
  )
}
